import { Link } from 'react-router-dom';
import {
  DatabaseBackup,
  HardDrive,
  ScrollText,
  Server,
  Settings,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePersonnalisation } from '../hooks/usePersonnalisation';

/**
 * L'administration, en cartes, pour le pouce.
 *
 * Sur ordinateur, ces sept écrans vivent dans le menu latéral : on les
 * survole, on les compare, on descend jusqu'au bon. Sur téléphone il n'y a
 * pas de menu latéral, et ils étaient donc simplement introuvables — un
 * président qui voulait vérifier ses sauvegardes depuis la salle des fêtes
 * devait attendre d'être rentré chez lui.
 *
 * D'où cette page, bâtie comme l'accès rapide : une carte par écran, avec ce
 * que chacun répond plutôt que son seul intitulé. « Sauvegardes » ne dit pas
 * grand-chose ; « vos données sont-elles à l'abri, et reste-t-il de la
 * place » le dit.
 *
 * Chaque carte suit le droit qui ouvre son écran : la page ne montre jamais
 * une porte qui se refermerait au clic.
 */
const TEINTES: Record<string, string> = {
  vert: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  bleu: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  ambre: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  violet: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  ardoise: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  rose: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

export default function AdministrationPage() {
  const { user, aLeDroit } = useAuth();
  const perso = usePersonnalisation();

  const estAdmin = user?.roles?.includes('ROLE_ADMIN') === true;
  const estSuperAdmin = user?.roles?.includes('ROLE_SUPER_ADMIN') === true;
  const boutiqueOuverte = (perso?.options as any)?.boutique !== false;

  const cartes = [
    ...(aLeDroit('voir_boutique') && boutiqueOuverte
      ? [{
          to: '/admin/boutique',
          icone: ShoppingBag,
          teinte: 'violet',
          titre: 'Boutique',
          quoi: 'Les articles, leurs tailles et leurs stocks. Le catalogue se tient une fois par saison.',
        }]
      : []),
    ...(estAdmin
      ? [{
          to: '/admin/parametres',
          icone: Settings,
          teinte: 'bleu',
          titre: 'Paramètres',
          quoi: "L'identité de l'association, ses coordonnées, ses couleurs, ses options et sa messagerie.",
        }]
      : []),
    ...(estAdmin
      ? [{
          to: '/admin/droits',
          icone: ShieldCheck,
          teinte: 'vert',
          titre: 'Gestion des droits',
          quoi: 'Ce que donne un statut, ce que donne une fonction, et qui tient quoi.',
        }]
      : []),
    ...(estAdmin
      ? [{
          to: '/admin/sauvegardes',
          icone: DatabaseBackup,
          teinte: 'vert',
          titre: 'Sauvegardes',
          quoi: 'Vos données sont-elles à l\'abri, et reste-t-il de la place ?',
        }]
      : []),
    ...(aLeDroit('gerer_fichiers')
      ? [{
          to: '/admin/fichiers',
          icone: HardDrive,
          teinte: 'ambre',
          titre: 'Fichiers déposés',
          quoi: 'Ce qui occupe la place, et ce qui ne sert plus à rien.',
        }]
      : []),
    ...(estAdmin
      ? [{
          to: '/admin/journal',
          icone: ScrollText,
          teinte: 'ardoise',
          titre: 'Journal des actions',
          quoi: 'Qui a fait quoi, et quand. À regarder quand une donnée a changé sans qu\'on sache pourquoi.',
        }]
      : []),
    ...(estAdmin
      ? [{
          to: '/admin/donnees',
          icone: ShieldCheck,
          teinte: 'rose',
          titre: 'Données personnelles',
          quoi: 'Les durées de conservation, le registre et l\'effacement à la demande.',
        }]
      : []),
    ...(estSuperAdmin
      ? [{
          to: '/admin/devops',
          icone: Server,
          teinte: 'ardoise',
          titre: 'Support technique',
          quoi: 'Réservé à l\'exploitant de la plateforme.',
        }]
      : []),
  ];

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Settings className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Administration
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Les réglages de l'espace, réunis pour le téléphone.
          </p>
        </div>
      </div>

      {cartes.length === 0 ? (
        <div className="card text-center py-10">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucun écran d'administration ne vous est ouvert.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cartes.map(({ to, icone: Icone, teinte, titre, quoi }) => (
            <Link key={to} to={to} className="card flex items-start gap-3 hover:border-primary-300 transition-colors">
              <span className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${TEINTES[teinte]}`}>
                <Icone className="w-5 h-5" />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-gray-900 dark:text-gray-100">{titre}</span>
                <span className="block text-sm text-gray-600 dark:text-gray-400 mt-0.5">{quoi}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
